// src/features/rooms/pages/RoomDetailsPage/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

// Hooks
import { useRooms } from '../../hooks/useRooms';
import { useEquipment } from '../../../equipment/hooks/useEquipment';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { usePermissions } from '../../../auth/hooks/usePermissions';

// Components
import RoomHeader from './components/RoomHeader';
import RoomDetailsCard from './components/RoomDetailsCard';
import RoomMetadataCard from './components/RoomMetadataCard';
import RoomEquipmentList from './components/RoomEquipmentList';
import RoomFilesSection from './components/RoomFilesSection';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import PageHeader from '../../../../components/common/PageHeader';

export default function RoomDetailsPage() {
  const { t } = useTranslation(['rooms', 'equipment', 'common']);
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { showError } = useSnackbar();
  const permissions = usePermissions();
  const { navigateBack } = useNavigationStack();
  const canReadEquipment = permissions.hasScope('equipment:read');

  // State
  const [room, setRoom] = useState(null);
  const [roomEquipment, setRoomEquipment] = useState([]);
  const [loading, setLoading] = useState(true);

  // API hooks
  const {
    fetchRoomById,
    error: roomError,
    clearError: clearRoomError,
  } = useRooms();

  const {
    fetchRoomEquipment,
    error: equipmentError,
  } = useEquipment();

  const loadRoomData = useCallback(async () => {
    if (!roomId) return;

    setLoading(true);
    try {
      const roomResult = await fetchRoomById(roomId);
      if (roomResult.success) {
        setRoom(roomResult.data);
      } else {
        showError(t('rooms:errors.load_failed'));
      }

      // Load equipment for this room
      if (canReadEquipment) {
        const equipmentResult = await fetchRoomEquipment(roomId, true);
        if (equipmentResult.success) {
          setRoomEquipment(equipmentResult.data.equipment || []);
        }
      }
    } catch (error) {
      showError(t('rooms:errors.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [fetchRoomById, fetchRoomEquipment, roomId, showError, t, canReadEquipment]);

  // Load room data
  useEffect(() => {
    loadRoomData();
  }, [roomId, fetchRoomById, fetchRoomEquipment, showError, t, loadRoomData]);

  const handleBack = useCallback(() => {
    navigateBack('/rooms');
  }, [navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/rooms/${roomId}/edit`);
  }, [navigate, roomId]);

  const handleViewEquipment = useCallback((equipmentId) => {
    navigate(`/equipment/${equipmentId}`);
  }, [navigate]);

  const handleAddFromTemplate = useCallback(() => {
    navigate(`/rooms/${roomId}/equipment/select-template`);
  }, [navigate, roomId]);

  const handleAddFromScratch = useCallback(() => {
    navigate(`/rooms/${roomId}/equipment/create`);
  }, [navigate, roomId]);

  const handleRoomUpdate = useCallback(async () => {
    // Reload room data after file update
    if (roomId) {
      const roomResult = await fetchRoomById(roomId);
      if (roomResult.success) {
        setRoom(roomResult.data);
      }
    }
  }, [roomId, fetchRoomById]);

  // Check permissions
  const canUpdate = permissions.hasScope('room:update');
  const canCreateEquipment = permissions.hasScope('equipment:create');
  const canUploadFiles = permissions.hasScope('file:upload');

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!room) {
    return (
      <Box>
        <PageHeader
          onBack={handleBack}
          title={t('rooms:messages.room_not_found')}
        />
        <Alert severity="error">
          {t('rooms:messages.room_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <RoomHeader 
        room={room}
        canUpdate={canUpdate}
        onBack={handleBack}
        onEdit={handleEdit}
      />

      {/* Error Alert */}
      {(roomError || equipmentError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearRoomError}>
          {roomError || equipmentError}
        </Alert>
      )}

      {/* Top Row - Room Details and Metadata */}
      <Box display="flex" gap={3} mb={3} alignItems="stretch" flexWrap={"wrap"}>
        <Box flex={2}>
          <RoomDetailsCard room={room} />
        </Box>

        <Box flex={1}>
          <RoomMetadataCard room={room} />
        </Box>
      </Box>

      {/* Bottom Row - Equipment and Files */}
      <Box display="flex" gap={3} alignItems={"stretch"} flexWrap={"wrap"}>
        {canReadEquipment && <Box flex={1}>
          <RoomEquipmentList
            roomEquipment={roomEquipment}
            canCreateEquipment={canCreateEquipment}
            onViewEquipment={handleViewEquipment}
            onAddFromTemplate={handleAddFromTemplate}
            onAddFromScratch={handleAddFromScratch}
          />
        </Box>}

        <Box flex={1}>
          <RoomFilesSection
            room={room}
            canUploadFiles={canUploadFiles}
            onRoomUpdate={handleRoomUpdate}
          />
        </Box>
      </Box>
    </Box>
  );
}